import { InputNumber } from "primereact/inputnumber";
import { classNames } from "primereact/utils";

type NumberFieldKind = 'currency' | 'percent' | 'integer' | 'decimal';

interface NumberFieldProps {
    value: number | null;
    /**
     * Se llama al confirmar el valor: al salir del campo, con Enter o con Tab (así dispara onValueChange el
     * InputNumber de PrimeReact 9.6). No se recalcula toda la factura en cada tecla.
     */
    onCommit: (value: number) => void;
    kind: NumberFieldKind;
    ariaLabel: string;
    id?: string;
    min?: number;
    max?: number;
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    /** Celda de tabla (32px) en vez de campo de formulario (36px). */
    inCell?: boolean;
    className?: string;
    describedBy?: string;
}

const KIND_PROPS: Record<NumberFieldKind, object> = {
    currency: { mode: 'currency', currency: 'ARS', locale: 'es-AR', minFractionDigits: 2, maxFractionDigits: 2 },
    percent: { mode: 'decimal', locale: 'es-AR', suffix: ' %', minFractionDigits: 0, maxFractionDigits: 2 },
    integer: { mode: 'decimal', locale: 'es-AR', useGrouping: false, maxFractionDigits: 0 },
    // Unidades de medicamentos y soporte nutricional: hasta 6 decimales, como las unidades de la línea.
    decimal: { mode: 'decimal', locale: 'es-AR', useGrouping: false, minFractionDigits: 0, maxFractionDigits: 6 },
};

/** Campo numérico de Novarium. El valor lo maneja el padre; el campo solo avisa cuando se confirma. */
export default function NumberField({ value, onCommit, kind, ariaLabel, id, min, max, placeholder, disabled, invalid, inCell, className, describedBy }: NumberFieldProps) {
    return (
        <InputNumber
            inputId={id}
            value={value}
            onValueChange={(e) => {
                // Pasar por un campo vacío sin escribir no es cargar un 0.
                if (e.value == null && value == null) return;
                const next = e.value ?? 0;
                if (next !== value) onCommit(next);
            }}
            min={min}
            max={max}
            placeholder={placeholder}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            className={classNames('nv-number', className, { 'nv-in-cell': inCell, 'p-invalid': invalid })}
            inputClassName={classNames({ 'p-filled': value !== null && value !== undefined })}
            {...KIND_PROPS[kind]}
        />
    );
}
